import React from "react";
import { Box, Typography, Select, MenuItem } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import IconCardSelect from "./IconCardSelect";
import { YesIcon, NoIcon } from "./onboardingIcons";
import { be_regions, lu_regions, fr_regions } from "bundles/common/utils/constants";

const countryRegionsMap = {
  belgium: be_regions,
  luxembourg: lu_regions,
  france: fr_regions,
};

const selectSx = {
  fontFamily: "'Segoe UI', system-ui, sans-serif",
  fontSize: "1rem",
  borderRadius: "0.5rem",
  backgroundColor: "hsl(0, 0%, 100%)",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "hsl(220, 13%, 71%)",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "hsl(220, 13%, 50%)",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "hsl(153, 68%, 25%)",
  },
};

const labelSx = {
  fontFamily: "'Segoe UI Semibold', system-ui, sans-serif",
  fontSize: "1rem",
  color: "hsl(222.2, 84%, 4.9%)",
  mb: "0.75rem",
  "& sup": {
    color: "hsl(0, 84.2%, 60.2%)",
    marginLeft: "0.15rem",
    fontSize: "0.75em",
    lineHeight: 0,
  },
};

const StepLocation = ({ values, errors, handleCardSelected, handleInputValue, resetField }) => {
  const intl = useIntl();

  const handleCountrySelected = (fieldName, value) => {
    handleCardSelected(fieldName, value);
    // Region lists differ per country — reset any previous choice silently
    // (no "required" error before the user even opened the region list).
    if (value !== values.country) {
      resetField("region");
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      {/* Country — 3 options, tap cards beat a dropdown */}
      <Box id="ob-field-country">
        <Typography sx={labelSx}>
          <FormattedMessage id="onboarding.country" />
          <sup>*</sup>
        </Typography>
        <IconCardSelect
          fieldName="country"
          selected={values.country}
          values={[
            { code: "belgium", label: intl.formatMessage({ id: "country.be" }) },
            { code: "luxembourg", label: intl.formatMessage({ id: "country.lu" }) },
            { code: "france", label: intl.formatMessage({ id: "country.fr" }) },
          ]}
          onChange={handleCountrySelected}
        />
        {errors.country && (
          <Typography sx={{ color: "hsl(0, 84.2%, 60.2%)", fontSize: "0.75rem", mt: "0.25rem" }}>
            {errors.country}
          </Typography>
        )}
      </Box>

      {/* Region */}
      <Box id="ob-field-region">
        <Typography sx={labelSx}>
          <FormattedMessage id="onboarding.region" />
          <sup>*</sup>
        </Typography>
        <Select
          fullWidth
          value={values.region || ""}
          onChange={(e) => handleInputValue({ target: { name: "region", value: e.target.value } })}
          name="region"
          displayEmpty
          variant="outlined"
          disabled={!values.country}
          sx={selectSx}
          renderValue={(selected) =>
            selected ? (
              intl.formatMessage({ id: `regions.${selected}`, defaultMessage: selected })
            ) : (
              <Box component="span" sx={{ color: "hsl(215.4, 16.3%, 46.9%)" }}>
                {values.country
                  ? intl.formatMessage({ id: "regions.region" })
                  : intl.formatMessage({ id: "onboarding.select_country_first", defaultMessage: "Sélectionne d'abord un pays" })}
              </Box>
            )
          }
        >
          {values.country && countryRegionsMap[values.country]?.length ? (
            countryRegionsMap[values.country].map((region) => (
              <MenuItem key={region} value={region}>
                {intl.formatMessage({ id: `regions.${region}`, defaultMessage: region })}
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>
              {intl.formatMessage({ id: "onboarding.select_country_first", defaultMessage: "Sélectionne d'abord un pays" })}
            </MenuItem>
          )}
        </Select>
        {errors.region && (
          <Typography sx={{ color: "hsl(0, 84.2%, 60.2%)", fontSize: "0.75rem", mt: "0.25rem" }}>
            {errors.region}
          </Typography>
        )}
      </Box>

      {/* Work permit */}
      <Box id="ob-field-work_permit">
        <Typography sx={labelSx}>
          <FormattedMessage id="onboarding.work_permit" />
          <sup>*</sup>
        </Typography>
        <IconCardSelect
          fieldName="work_permit"
          selected={values.work_permit}
          values={[
            { code: true, icon: YesIcon, label: intl.formatMessage({ id: "onboarding.yes_box" }) },
            { code: false, icon: NoIcon, label: intl.formatMessage({ id: "onboarding.no_box" }) },
          ]}
          onChange={handleCardSelected}
        />
      </Box>
    </Box>
  );
};

export default StepLocation;
